// Docs: IMPLEMENTATION.md §3.1 (entry, routing). Also touches FEATURES.md
// (every page-level route corresponds to a feature). Keep both in sync when
// adding routes or changing auth gating.
//
// One route table, two shells (useShell, breakpoint 768px):
//   Coach  (<768):  CoachTabBar — 3 tabs + center ▶ (useNextLesson)
//   Studio (≥768):  StudioRail sidebar + CommandPalette (⌘K)
// Auth pages (/login, /oauth/consent) and the onboarding wizard
// (/onboarding) render without any shell. /practice/:lessonId is the lesson
// runner, not a page: while it is mounted both shells hide via
// html[data-lesson-active] (see index.css). Lessons are listed and created on
// Heute (`/?erstellen` opens the Erstellen window) — see utils/lessonRoutes.
import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import toast, { Toaster, ToastBar } from 'react-hot-toast';
import { register as registerTimeago } from 'timeago.js';
import timeagoDe from 'timeago.js/lib/lang/de';

// Relative timestamps ("vor 2 Stunden") render in German app-wide.
registerTimeago('de', timeagoDe.default || timeagoDe);
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from './queryClient';
import { persistOptions } from './queryPersistence';
import StudioRail from './components/StudioRail';
import CoachTabBar from './components/CoachTabBar';
import CommandPalette from './components/CommandPalette';
import MissingApiKeyBanner from './components/MissingApiKeyBanner';
import OperationsTray from './components/OperationsTray';
import DebugWindow from './components/DebugWindow';
import WakeGate from './components/WakeGate';
import { useShell } from './hooks/useShell';
import { GenericPageSkeleton } from './components/SkeletonScreens';
import LegacyPracticeRedirect from './components/LegacyPracticeRedirect';
import { trackPageview } from './analytics';

// Lazy-loaded route components
const Home = lazy(() => import('./components/Home'));
const WordManager = lazy(() => import('./components/WordManager'));
const PoolScout = lazy(() => import('./components/PoolScout'));
const WordLookup = lazy(() => import('./components/WordLookup'));
const Lesson = lazy(() => import('./components/Lesson'));
const DiscoverHub = lazy(() => import('./components/DiscoverHub'));
const Stats = lazy(() => import('./components/Stats'));
const Settings = lazy(() => import('./components/Settings'));
const ProfilePage = lazy(() => import('./components/ProfilePage'));
const NotFound = lazy(() => import('./components/NotFound'));
const Login = lazy(() => import('./components/Login'));
const OAuthConsent = lazy(() => import('./components/OAuthConsent'));
const Onboarding = lazy(() => import('./components/Onboarding'));


// Authentication wrapper component
const AuthWrapper = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem('jwt');
    const isLoginPage = location.pathname === '/login';

    if (!token && !isLoginPage) {
      navigate('/login');
    }
  }, [navigate, location]);

  return children;
};

// Old /learn deep-links → /discover, keeping ?tab= (aliases resolve there).
const LegacyLearnRedirect = () => {
  const location = useLocation();
  return <Navigate to={{ pathname: '/discover', search: location.search }} replace />;
};

const AppContent = ({ operationIds, onOperationDone, localOps, onLocalOpDone }) => {
  const shell = useShell();
  const location = useLocation();
  const isAuthPage =
    location.pathname === '/login' || location.pathname === '/oauth/consent';
  // The onboarding wizard is a focus-mode page like a running lesson: no
  // shells (but MissingApiKeyBanner stays — it renders outside withShell and
  // the wizard's no-key branch points at it).
  const isBarePage = isAuthPage || location.pathname === '/onboarding';
  const withShell = !isBarePage;

  useEffect(() => {
    trackPageview(location.pathname + location.search);
  }, [location.pathname, location.search]);

  return (
    <div className="app">
      {withShell && shell === 'studio' && <StudioRail />}
      <MissingApiKeyBanner />
      <main className={`main ${withShell && shell === 'studio' ? 'main--studio' : ''}`}>
        <div className="w-full">
          <Suspense fallback={<GenericPageSkeleton />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/home" element={<Home />} />
              <Route path="/words" element={<WordManager />} />
              <Route path="/words/scout" element={<PoolScout />} />
              <Route path="/words/lookup" element={<WordLookup />} />
              <Route path="/discover" element={<DiscoverHub />} />
              <Route path="/learn" element={<LegacyLearnRedirect />} />
              <Route path="/practice/:lessonId" element={<Lesson />} />
              <Route path="/practice" element={<LegacyPracticeRedirect />} />
              <Route path="/stats" element={<Stats />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/create-lesson" element={<LegacyPracticeRedirect />} />
              <Route path="/drills" element={<LegacyPracticeRedirect />} />
              <Route path="/login" element={<Login />} />
              <Route path="/oauth/consent" element={<OAuthConsent />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </div>
      </main>

      {withShell && shell === 'coach' && <CoachTabBar />}
      {withShell && shell === 'studio' && <CommandPalette />}

      <OperationsTray
        operationIds={operationIds}
        onOperationDone={onOperationDone}
        localOps={localOps}
        onLocalOpDone={onLocalOpDone}
      />

      {/* One toaster for the whole app (pages only fire toast()) — Bauhaus:
          square, ink border, no shadow. */}
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            borderRadius: 0,
            border: '1.5px solid var(--ink-900)',
            background: 'var(--surface)',
            color: 'var(--ink-900)',
            boxShadow: 'none',
          },
          success: { iconTheme: { primary: 'var(--success)', secondary: 'var(--cream)' } },
          error: { iconTheme: { primary: 'var(--error)', secondary: 'var(--cream)' } },
        }}
      >
        {(t) => (
          <ToastBar toast={t}>
            {({ icon, message }) => (
              <div className="flex items-center">
                {icon}
                <div className="mx-2">{message}</div>
                {t.type !== 'loading' && (
                  <button
                    onClick={() => toast.dismiss(t.id)}
                    className="ml-2 text-[var(--ink-500)] hover:text-[var(--ink-800)]"
                    aria-label="Schließen"
                  >
                    ✕
                  </button>
                )}
              </div>
            )}
          </ToastBar>
        )}
      </Toaster>
    </div>
  );
};

function App() {
  // Every operation announced via `lro:start` is tracked until it finishes
  // or is dismissed, so several operations can run (and render) at once.
  const [operationIds, setOperationIds] = useState([]);
  // Client-driven progress (no backend operation id) announced via
  // `lro:local`: the sender pushes {id, title, current, total, text, status}
  // snapshots; they render in the same bottom-right OperationsTray.
  const [localOps, setLocalOps] = useState([]);
  const [showDebugWindow, setShowDebugWindow] = useState(false);


  // Expose a simple event-based way to register operation ids globally
  useEffect(() => {
    const handler = (e) => {
      if (e && e.detail && e.detail.operationId) {
        const id = e.detail.operationId;
        setOperationIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
      }
    };
    window.addEventListener('lro:start', handler);
    return () => window.removeEventListener('lro:start', handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const detail = e && e.detail;
      if (!detail || !detail.id) return;
      setLocalOps((prev) => {
        const entry = { status: 'active', ...detail };
        const idx = prev.findIndex((op) => op.id === detail.id);
        if (idx === -1) return [...prev, entry];
        const next = [...prev];
        next[idx] = entry;
        return next;
      });
    };
    window.addEventListener('lro:local', handler);
    return () => window.removeEventListener('lro:local', handler);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.ctrlKey && event.key.toLowerCase() === 'd') {
        event.preventDefault();
        setShowDebugWindow((prev) => !prev);
      }
    };
    const handleToggle = () => setShowDebugWindow((prev) => !prev);
    const handleOpen = () => setShowDebugWindow(true);

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('debug:toggle', handleToggle);
    window.addEventListener('debug:open', handleOpen);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('debug:toggle', handleToggle);
      window.removeEventListener('debug:open', handleOpen);
    };
  }, []);

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={persistOptions}
    >
      <Router>
        <AuthWrapper>
          <AppContent
            operationIds={operationIds}
            onOperationDone={(operationId) =>
              setOperationIds((prev) => prev.filter((id) => id !== operationId))
            }
            localOps={localOps}
            onLocalOpDone={(localOpId) =>
              setLocalOps((prev) => prev.filter((op) => op.id !== localOpId))
            }
          />
          <DebugWindow
            isOpen={showDebugWindow}
            onClose={() => setShowDebugWindow(false)}
          />
          {/* Covers stale data while the Railway api wakes up. */}
          <WakeGate />
        </AuthWrapper>
      </Router>
      <ReactQueryDevtools initialIsOpen={false} />
    </PersistQueryClientProvider>
  );
}

export default App;
